import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledRecommendationsSection = styled.section`
  max-width: 900px;

  h2 {
    margin-bottom: 40px;
  }
`;

const StyledRecommendationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  grid-gap: 25px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StyledMoreButton = styled(Link)`
  ${({ theme }) => theme.mixins.button};
  margin: 80px auto 0;
  display: block;
  width: fit-content;
`;

const StyledRecommendationCard = styled.div`
  ${({ theme }) => theme.mixins.boxShadow};
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 30px;
  border-radius: var(--border-radius);
  background-color: var(--light-navy);
  transition: var(--transition);

  &:hover {
    transform: translateY(-5px);
  }
`;

const StyledQuote = styled.blockquote`
  margin: 0 0 25px 0;
  padding: 0;
  border: none;
  border-left: none;
  color: var(--light-slate);
  font-size: var(--fz-md);
  line-height: 1.6;
  font-style: italic;

  &:before {
    content: '"';
    color: var(--green);
    font-size: 40px;
    line-height: 0;
    vertical-align: -15px;
    margin-right: 5px;
  }
`;

const StyledAuthor = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
`;

const StyledAvatar = styled.div`
  position: relative;
  width: 60px;
  height: 60px;
  margin-right: 15px;
  flex-shrink: 0;

  .avatar-wrapper {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;

    .img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }
  }
`;

const StyledAuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledAuthorName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h4 {
    margin: 0;
    color: var(--lightest-slate);
    font-size: var(--fz-lg);
    font-weight: 600;
  }

  a {
    ${({ theme }) => theme.mixins.flexCenter};
    color: var(--green);
    transition: var(--transition);

    &:hover {
      color: var(--green);
      transform: translateY(-2px);
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const StyledAuthorTitle = styled.p`
  margin: 12px 0 0 0;
  color: var(--slate);
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
`;

const Recommendations = () => {
  const data = useStaticQuery(graphql`
    {
      recommendations: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/recommendations/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              name
              title
              company
              linkedin
              avatar {
                childImageSharp {
                  gatsbyImageData(width: 120, height: 120, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
            }
            html
          }
        }
      }
    }
  `);

  const recommendations = data.recommendations.edges;
  const displayedRecommendations = recommendations.slice(0, 2);
  const revealTitle = useRef(null);
  const revealCards = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealCards.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  return (
    <StyledRecommendationsSection id="recommendations">
      <h2 className="numbered-heading" ref={revealTitle}>
        Recommendations
      </h2>

      <StyledRecommendationsGrid>
        {displayedRecommendations &&
          displayedRecommendations.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { name, title, company, linkedin, avatar } = frontmatter;
            const image = getImage(avatar);

            return (
              <StyledRecommendationCard key={i} ref={el => (revealCards.current[i] = el)}>
                <StyledQuote dangerouslySetInnerHTML={{ __html: html.replace(/<\/?p>/g, '') }} />

                <StyledAuthor>
                  {image && (
                    <StyledAvatar>
                      <div className="avatar-wrapper">
                        <GatsbyImage image={image} alt={name} className="img" />
                      </div>
                    </StyledAvatar>
                  )}

                  <StyledAuthorInfo>
                    <StyledAuthorName>
                      <h4>{name}</h4>
                      {linkedin && (
                        <a
                          href={linkedin}
                          aria-label={`${name}'s LinkedIn`}
                          target="_blank"
                          rel="noopener noreferrer">
                          <Icon name="Linkedin" />
                        </a>
                      )}
                    </StyledAuthorName>
                    <StyledAuthorTitle>
                      {title}{company && ` @ ${company}`}
                    </StyledAuthorTitle>
                  </StyledAuthorInfo>
                </StyledAuthor>
              </StyledRecommendationCard>
            );
          })}
      </StyledRecommendationsGrid>

      {recommendations.length > 2 && (
        <StyledMoreButton to="/recommendations">View All</StyledMoreButton>
      )}
    </StyledRecommendationsSection>
  );
};

export default Recommendations;
